import { useCallback, useEffect, useState } from 'react'
import { businessTimeZone } from '../lib/businessTime'
import {
  AlertCircle,
  Building2,
  Check,
  Gem,
  Loader2,
  Network,
  Pencil,
  RefreshCw,
  Settings2,
  ShieldCheck,
  ShieldQuestion,
  Sparkles,
  Trash2,
  UserRound,
} from 'lucide-react'
import { AnalystNavigation } from '../components/Navigation'
import { SocShell } from '../components/soc/SocShell'
import {
  correctPoLEntry,
  getPatternsOfLife,
  type PatternsOfLifeEntry,
} from '../lib/api'

/**
 * "What your team knows about your world" — the patterns-of-life read-back.
 *
 * PRODUCT_VISION: the patterns the team has learned, shown back as evidence
 * of care ("Here's what I know about your world") — and this read-back
 * DOUBLES as the way the client CORRECTS it ("no, that's my new accountant,
 * not suspicious"). Each fact card carries three plain-language affordances:
 *   • "That's expected"  → not_suspicious (the client vouches; Run-3 weighting)
 *   • "Correct this"      → correct (inline editor with the right fact)
 *   • "Remove"            → remove (soft-delete; the card leaves the list)
 * These call POST /onboarding/patterns-of-life/{id}/correct (deterministic,
 * no LLM). The provenance the backend returns (correction_status / corrected_at)
 * is shown back — and it comes in the GET list too, so corrected & confirmed
 * states persist across reloads. A 'removed' entry is still returned by the
 * soft-delete backend, so this client view filters it out to match the team's.
 *
 * AUDIENCE (DW-CLIENT-POL-READBACK): the page itself is already written in
 * the client's voice — "what YOUR team knows about YOUR world" — so the client
 * mount at /client/world is the SAME page, not a 700-line clone. Only the
 * shell chrome differs: `audience="client"` wears the client nav (so the
 * customer can get back to home, alerts, the Concierge) instead of the analyst
 * console nav. Everything below the nav — the fetch, the sections, and the
 * three correction affordances — is one implementation for both audiences, so
 * a fix to the read-back can never land for only one of them. The analyst
 * mount at /analyst/knowledge is unchanged (audience defaults to 'analyst').
 *
 * Honesty rules (§7): never invent knowledge. An empty result says nothing
 * is written yet, not "all known" — and never claims the team is watching
 * or that the page will fill in on its own. A fetch failure says we couldn't reach
 * the team — never a fake, reassuring blank page. A failed correction leaves
 * the card unchanged with an honest inline line — never toast theatre or a
 * faked "saved" state. And the sensitive escalation contact_details key is
 * never rendered.
 */

// ── Safe accessors over the opaque, section-varying `data` record ──────

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null
}

function asStringList(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.map(asString).filter((s): s is string => s !== null)
}

function asRecordList(v: unknown): Record<string, unknown>[] {
  if (!Array.isArray(v)) return []
  return v.filter(
    (x): x is Record<string, unknown> =>
      typeof x === 'object' && x !== null && !Array.isArray(x),
  )
}

/** Join non-empty fragments into one plain-language line. */
function sentence(parts: (string | null)[]): string {
  return parts.filter((p): p is string => !!p).join(' · ')
}

// ── Section presentation ───────────────────────────────────────────────

const SECTION_META: Record<
  string,
  { title: string; blurb: string; Icon: typeof UserRound; order: number }
> = {
  people: {
    title: 'The people in your world',
    blurb: 'Who they are, and how they normally work.',
    Icon: UserRound,
    order: 1,
  },
  relationships: {
    title: 'Who works with whom',
    blurb: 'The relationships and outside contacts we expect to see.',
    Icon: Network,
    order: 2,
  },
  operations: {
    title: 'How your world normally runs',
    blurb: 'The everyday IT and admin practices that are normal for you.',
    Icon: Settings2,
    order: 3,
  },
  sensitivities: {
    title: 'What matters most',
    blurb: 'The things worth guarding.',
    Icon: Gem,
    order: 4,
  },
  escalation_policy: {
    title: 'Who we reach, and when',
    blurb: 'How your team gets hold of the right person.',
    Icon: Building2,
    order: 5,
  },
  response_consent: {
    title: 'Your preferences on file',
    blurb: 'Captured for the future: noted, never acted on.',
    Icon: ShieldQuestion,
    order: 6,
  },
}

function metaFor(section: string) {
  return (
    SECTION_META[section] ?? {
      title: section.replace(/_/g, ' '),
      blurb: 'What we have learned so far.',
      Icon: Sparkles,
      order: 99,
    }
  )
}

/** Format an ISO timestamp as a warm, plain provenance date ("12 Aug 2026").
 * Returns '' for a missing/unparseable value so provenance never shows junk. */
function formatDate(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-NZ', {
    timeZone: businessTimeZone(),
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** The plain text of a card's body — used to prefill the "Correct this"
 * editor with what the fact currently says. */
function cardText(title: string, lines: string[]): string {
  const body = lines.filter((l) => l.trim() !== '')
  return body.length > 0 ? body.join('\n') : title
}

type CardMode = 'idle' | 'editing' | 'confirmingRemove'

/**
 * One patterns-of-life entry, shown as a plain-language fact card that the
 * client can vouch for, correct, or remove. All three actions POST to the
 * corrections endpoint; on success the parent swaps in the updated entry (a
 * 'removed' entry then filters out of the list). On failure the card is left
 * exactly as it was, with an honest inline line.
 */
function KnowledgeCard({
  entry,
  onUpdated,
}: {
  entry: PatternsOfLifeEntry
  onUpdated: (updated: PatternsOfLifeEntry) => void
}) {
  const [mode, setMode] = useState<CardMode>('idle')
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const primary = entryCards(entry)[0] ?? {
    title: metaFor(entry.section).title,
    lines: [],
  }
  const status = entry.correction_status ?? null
  const corrected = status === 'corrected'
  const confirmedNormal = status === 'confirmed_normal'
  const correctionText =
    (corrected ? asString(entry.data?.client_correction) : null) ?? null
  const when = formatDate(entry.corrected_at)

  // What the card currently says (the corrected text once corrected, else the
  // derived fact lines) — also the prefill for the inline editor.
  const bodyLines = correctionText
    ? [correctionText]
    : primary.lines.filter((l) => l.trim() !== '')

  const runAction = useCallback(
    async (
      action: 'not_suspicious' | 'remove' | 'correct',
      text: string,
    ) => {
      setBusy(true)
      setError(null)
      try {
        const updated = await correctPoLEntry(entry.id, action, text)
        onUpdated(updated)
        setMode('idle')
        setDraft('')
      } catch {
        // Honest, quiet failure: the card is unchanged and we say so. No toast,
        // no fake "saved" state (§7 — never a false all-clear).
        setError("We couldn't save that just now, and nothing's changed. Please try again.")
      } finally {
        setBusy(false)
      }
    },
    [entry.id, onUpdated],
  )

  const openEditor = useCallback(() => {
    setDraft(correctionText ?? cardText(primary.title, primary.lines))
    setError(null)
    setMode('editing')
  }, [correctionText, primary.title, primary.lines])

  const saveCorrection = useCallback(() => {
    const text = draft.trim()
    if (!text) {
      setError('Please tell your team the right detail before saving.')
      return
    }
    void runAction('correct', text)
  }, [draft, runAction])

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="font-medium text-navy">{primary.title}</p>

      {bodyLines.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {bodyLines.map((line, i) => (
            <li key={i} className="text-sm text-gray-600">
              {line}
            </li>
          ))}
        </ul>
      )}

      {/* Provenance — shown back so the client sees their own edit stuck. */}
      {corrected && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-brass">
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          You corrected this{when ? ` on ${when}` : ''}.
        </p>
      )}
      {confirmedNormal && (
        <div className="mt-2 rounded-lg bg-green-50 px-3 py-2">
          <p className="inline-flex items-center gap-1.5 text-xs font-medium text-green-800">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Marked as expected{when ? ` on ${when}` : ''}, so your team won't keep
            flagging this.
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}

      {/* ── Affordances ────────────────────────────────────────────── */}
      {mode === 'idle' && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          {!confirmedNormal && (
            <button
              type="button"
              onClick={() => void runAction('not_suspicious', '')}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 transition-colors hover:border-green-300 hover:text-green-800 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              That's expected
            </button>
          )}
          <button
            type="button"
            onClick={openEditor}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 transition-colors hover:border-brass hover:text-navy disabled:opacity-50"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            Correct this
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null)
              setMode('confirmingRemove')
            }}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-red-600 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            Remove
          </button>
        </div>
      )}

      {mode === 'editing' && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <label className="text-xs text-gray-500" htmlFor={`correct-${entry.id}`}>
            What should your team know instead?
          </label>
          <textarea
            id={`correct-${entry.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            disabled={busy}
            maxLength={2000}
            className="mt-1 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-navy focus:border-brass focus:outline-none disabled:opacity-50"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={saveCorrection}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-3 py-1.5 text-xs text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              Save correction
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('idle')
                setError(null)
              }}
              disabled={busy}
              className="text-xs text-gray-500 transition-colors hover:text-navy disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === 'confirmingRemove' && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-600">
            Remove this from what your team knows? They'll stop showing it.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void runAction('remove', '')}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              Yes, remove it
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('idle')
                setError(null)
              }}
              disabled={busy}
              className="text-xs text-gray-500 transition-colors hover:text-navy disabled:opacity-50"
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Turn one stored entry into one or more fact cards for its section. */
function entryCards(entry: PatternsOfLifeEntry): { title: string; lines: string[] }[] {
  const d = entry.data ?? {}

  switch (entry.section) {
    case 'people': {
      const name = asString(d.name) ?? entry.subject ?? 'Someone in your world'
      const rhythm = asString(d.work_rhythm)
      const where = asString(d.home_location)
      const tz = asString(d.timezone)
      const travel = asString(d.travel_pattern)
      const authority = asString(d.authority)
      const devices = asStringList(d.normal_devices)
      // Headline sentence, e.g. "CFO · usually Auckland, weekday mornings".
      const headline = sentence([
        asString(d.role),
        where && rhythm
          ? `usually ${where}, ${rhythm}`
          : where
            ? `usually signs in from ${where}`
            : rhythm,
      ])
      const lines = [
        headline,
        tz ? `Time zone: ${tz}` : '',
        travel ? `Travels: ${travel}` : '',
        authority ? `Normally can: ${authority}` : '',
        devices.length ? `Usual devices: ${devices.join(', ')}` : '',
      ]
      return [{ title: name, lines }]
    }

    case 'relationships': {
      const internal = asStringList(d.normal_internal)
      const assistants = asStringList(d.assistants)
      const approvers = asStringList(d.approvers)
      const external = asRecordList(d.key_external).map((c) =>
        sentence([
          asString(c.name),
          asString(c.relationship),
          asString(c.domain),
        ]),
      )
      return [
        {
          title: 'Working relationships',
          lines: [
            internal.length ? `Work together: ${internal.join('; ')}` : '',
            approvers.length ? `Approves things: ${approvers.join('; ')}` : '',
            assistants.length ? `Assists: ${assistants.join('; ')}` : '',
            ...external.map((e) => `Outside contact: ${e}`),
          ],
        },
      ]
    }

    case 'operations': {
      const practices = asStringList(d.normal_practices)
      const contacts = asStringList(d.it_contacts)
      return [
        {
          title: 'Everyday practices',
          lines: [
            ...practices.map((p) => `Normal here: ${p}`),
            contacts.length ? `Who does IT: ${contacts.join(', ')}` : '',
          ],
        },
      ]
    }

    case 'sensitivities': {
      const jewels = asStringList(d.crown_jewels)
      const watch = asStringList(d.watch_list)
      return [
        {
          title: 'What we guard',
          lines: [
            ...jewels.map((j) => `Worth guarding: ${j}`),
            ...watch.map((w) => `Handled with extra care: ${w}`),
          ],
        },
      ]
    }

    case 'escalation_policy': {
      // Sensitive contact_details (§7) are deliberately NOT rendered.
      const contacts = asRecordList(d.contacts).map((c) => {
        const parts = sentence([asString(c.name), asString(c.role)])
        const situations = asStringList(c.situations)
        const channels = asStringList(c.channels)
        const bits = [
          parts,
          situations.length ? `for ${situations.join(', ')}` : '',
          channels.length ? `via ${channels.join('/')}` : '',
          c.wake_at_3am === true ? '(may be called out of hours)' : '',
        ].filter((b) => b !== '')
        return bits.join(' ')
      })
      const notes = asString(d.notes)
      return [
        {
          title: 'Reaching the right person',
          lines: [...contacts, notes ? `Note: ${notes}` : ''],
        },
      ]
    }

    case 'response_consent': {
      // Captured, DISARMED (§7): shown as a noted preference, never a control.
      const consents = asRecordList(d.consents).map((c) =>
        sentence([asString(c.action), asString(c.condition), asString(c.notes)]),
      )
      return [
        {
          title: 'Your preferences',
          lines: [
            ...consents.map((c) => `You told us: ${c}`),
            'Noted for the future: nothing here is armed or acted on.',
          ],
        },
      ]
    }

    default: {
      // Unknown section: render its string-ish fields plainly rather than
      // inventing structure. Never dumps raw JSON at the client.
      const lines = Object.entries(d)
        .map(([k, v]) => {
          const s = asString(v)
          if (s) return `${k.replace(/_/g, ' ')}: ${s}`
          const list = asStringList(v)
          return list.length ? `${k.replace(/_/g, ' ')}: ${list.join(', ')}` : ''
        })
        .filter((l) => l !== '')
      return [{ title: entry.subject ?? metaFor(entry.section).title, lines }]
    }
  }
}

// ── Load state ─────────────────────────────────────────────────────────

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; entries: PatternsOfLifeEntry[] }

/** Which shell this read-back is mounted inside. The page body is identical
 * for both; only the nav chrome changes. */
export type KnowledgeAudience = 'analyst' | 'client'

export function TeamKnowledgePage({
  audience = 'analyst',
}: {
  audience?: KnowledgeAudience
} = {}) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [isRefreshing, setIsRefreshing] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await getPatternsOfLife()
      setState({ kind: 'ready', entries: res.entries ?? [] })
    } catch {
      // No infinite spinner, and no fake blank "all known" page — say plainly
      // that we couldn't reach the team.
      setState({ kind: 'error' })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await load()
    setIsRefreshing(false)
  }, [load])

  // Swap in an entry the client just corrected/vouched-for/removed. A 'removed'
  // entry stays in state (the backend soft-deletes it) but is filtered out of
  // the view below — the card leaves the list and stays gone on reload.
  const handleUpdated = useCallback((updated: PatternsOfLifeEntry) => {
    setState((prev) =>
      prev.kind === 'ready'
        ? {
            kind: 'ready',
            entries: prev.entries.map((e) =>
              e.id === updated.id ? updated : e,
            ),
          }
        : prev,
    )
  }, [])

  const page = (
    <>
        <header className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-2xl text-navy">
                What your team knows about your world
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                The patterns your security team has on file, shown back to
                you.
              </p>
            </div>
            {state.kind !== 'loading' && (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 transition-colors hover:text-navy disabled:opacity-50"
                aria-label="Refresh"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                Refresh
              </button>
            )}
          </div>
        </header>

        {state.kind === 'loading' && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-brass" />
              <p className="text-sm text-gray-500">
                Gathering what your team knows...
              </p>
            </div>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
            <AlertCircle
              className="mx-auto h-8 w-8 text-gray-400"
              aria-hidden="true"
            />
            <h2 className="mt-3 text-lg font-medium text-navy">
              Couldn't reach your team right now
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This isn't a sign anything is wrong. We just couldn't load what
              your team knows this moment. Please try again.
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-navy px-4 py-2 text-sm text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              Try again
            </button>
          </div>
        )}

        {state.kind === 'ready' && visibleEntries(state.entries).length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
            <Sparkles
              className="mx-auto h-8 w-8 text-brass"
              aria-hidden="true"
            />
            <h2 className="mt-3 text-lg font-medium text-navy">
              Nothing is written here yet
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your team hasn't recorded anything about the people, rhythms
              and relationships in your world. When they have notes, they'll
              show here.
            </p>
          </div>
        )}

        {state.kind === 'ready' && visibleEntries(state.entries).length > 0 && (
          <div className="space-y-8">
            {groupBySection(visibleEntries(state.entries)).map(
              ({ section, entries }) => {
                const meta = metaFor(section)
                return (
                  <section key={section}>
                    <div className="mb-3 flex items-center gap-2">
                      <meta.Icon
                        className="h-5 w-5 text-brass"
                        aria-hidden="true"
                      />
                      <div>
                        <h2 className="font-serif text-lg text-navy">
                          {meta.title}
                        </h2>
                        <p className="text-xs text-gray-500">{meta.blurb}</p>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {entries.map((entry) => (
                        <KnowledgeCard
                          key={entry.id}
                          entry={entry}
                          onUpdated={handleUpdated}
                        />
                      ))}
                    </div>
                  </section>
                )
              },
            )}
          </div>
        )}
    </>
  )

  // BRIEF-E5d: the customer sees this page in the one customer header.
  if (audience === 'client') {
    return (
      <SocShell current="world" mainClassName="box-border w-full max-w-4xl mx-auto px-4 py-8 sm:px-6">
        {page}
      </SocShell>
    )
  }
  return (
    <div className="min-h-screen bg-cream">
      <AnalystNavigation />
      <main className="max-w-4xl mx-auto px-6 py-8">{page}</main>
    </div>
  )
}

/** Entries the client should still see — a 'removed' entry is soft-deleted on
 * the backend but still returned by GET, so we hide it here to match the team's
 * own view (the card left the list and stays gone on reload). */
function visibleEntries(
  entries: PatternsOfLifeEntry[],
): PatternsOfLifeEntry[] {
  return entries.filter((e) => e.correction_status !== 'removed')
}

/** Group entries by section, ordered by SECTION_META. */
function groupBySection(
  entries: PatternsOfLifeEntry[],
): { section: string; entries: PatternsOfLifeEntry[] }[] {
  const bySection = new Map<string, PatternsOfLifeEntry[]>()
  for (const entry of entries) {
    const list = bySection.get(entry.section) ?? []
    list.push(entry)
    bySection.set(entry.section, list)
  }
  return Array.from(bySection.entries())
    .map(([section, list]) => ({ section, entries: list }))
    .sort((a, b) => metaFor(a.section).order - metaFor(b.section).order)
}
